
import { isValidUuid } from './imageUtils';

// Cache for storing main profile images to avoid excessive calls
const mainImageCache = new Map<string, string | null>();

// Cache for storing public URLs to avoid regenerating them
const publicUrlCache = new Map<string, string>();

// Cache lifetime in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

// Cache timestamps for managing expiration
const cacheTimestamps = new Map<string, number>();

// Set of processed non-UUID IDs to avoid repeated checks
const processedNonUuidIds = new Set<string>();

/**
 * Function to check if a cache entry is expired
 */
export const isCacheExpired = (key: string): boolean => {
  const timestamp = cacheTimestamps.get(key);
  if (!timestamp) return true;
  return Date.now() - timestamp > CACHE_TTL;
};

/**
 * Get a value from the main image cache
 */
export const getFromMainImageCache = (profileId: string): string | null | undefined => {
  if (mainImageCache.has(profileId) && !isCacheExpired(profileId)) {
    return mainImageCache.get(profileId);
  }
  return undefined;
};

/**
 * Set a value in the main image cache
 */
export const setMainImageCache = (profileId: string, imageUrl: string | null): void => {
  mainImageCache.set(profileId, imageUrl);
  cacheTimestamps.set(profileId, Date.now());
};

/**
 * Clear an entry from the main image cache
 */
export const clearMainImageCache = (profileId: string): void => {
  mainImageCache.delete(profileId);
  cacheTimestamps.delete(profileId);
};

/**
 * Check if a profile ID has been processed as a non-UUID
 */
export const isProcessedNonUuid = (profileId: string): boolean => {
  return processedNonUuidIds.has(profileId);
};

/**
 * Mark a profile ID as processed non-UUID
 */
export const markAsProcessedNonUuid = (profileId: string): void => {
  processedNonUuidIds.add(profileId);
};

/**
 * Get a public URL from the cache
 */
export const getPublicUrlFromCache = (path: string): string | undefined => {
  if (publicUrlCache.has(path) && !isCacheExpired(path)) {
    return publicUrlCache.get(path);
  }
  return undefined;
};

/**
 * Store a public URL in the cache
 */
export const setPublicUrlCache = (path: string, url: string): void => {
  publicUrlCache.set(path, url);
  cacheTimestamps.set(path, Date.now());
};
